import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { deleteRequest, postRequest } from '../../../api/ApiController';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { LocaleContext } from '../../LocaleContext';
import UtilContext from '../../UtilContext';
import { CrmPanelShell } from './panelLayout';
import { runCrmPanelMutation, showCrmWorkflowToast } from '../../../utils/crmCommunicationPanelSave';

function userLabel(u) {
  return u?.employee?.name || u?.username || `ID ${u?.id ?? ''}`;
}

/**
 * @param {{ ticketId: number, ticketType?: string, participants?: Array<Record<string, unknown>>, onChanged: () => void, readOnly?: boolean, embedded?: boolean }} props
 */
export default function CrmCommunicationParticipantsPanel({ ticketId, ticketType = 'announcement', participants, onChanged, readOnly = false, embedded = false }) {
  const { t } = useContext(LocaleContext);
  const { toast } = useContext(UtilContext);
  const [state] = useContext(DatabaseContext);
  const [involveTicket, setInvolveTicket] = useState(false);
  const [involveUser, setInvolveUser] = useState(null);
  const [busy, setBusy] = useState(false);
  const p = Array.isArray(participants) ? participants : [];

  /** Parita ServiceTaskDetail – všetci používatelia, nie len aktívni obchodníci. */
  const userOptions = useMemo(
    () => (state?.users || [])
      .map((u) => ({
        label: userLabel(u),
        value: Number(u.id),
        name: userLabel(u),
        id: Number(u.id),
      }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label))),
    [state?.users],
  );

  const participantName = useCallback(
    (row) => {
      const u = row?.user;
      if (u) return userLabel(u);
      const id = row?.user_id;
      const found = (state?.users || []).find((x) => Number(x.id) === Number(id));
      return found ? userLabel(found) : `user #${id}`;
    },
    [state?.users],
  );

  const addUser = async (userId) => {
    if (!userId) {
      toast?.current?.show({ severity: 'warn', summary: t('CrmCommunication.Participants.SelectUser'), life: 2200 });
      return;
    }
    setBusy(true);
    try {
      await runCrmPanelMutation(onChanged, async () => {
        const res = await postRequest(`/api/crm_communication_tickets/${ticketId}/participants`, { user_id: userId, involved: true }, true);
        if (res?.status === 200) {
          showCrmWorkflowToast(toast, t, 'ServiceTaskDetail.Toast.UserAdded');
          return res;
        } else {
          throw new Error(JSON.stringify(res?.errors || res));
        }
      });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
    } finally {
      setBusy(false);
    }
  };

  const removeUser = async (userId) => {
    setBusy(true);
    try {
      await runCrmPanelMutation(onChanged, async () => {
        const res = await deleteRequest(`/api/crm_communication_tickets/${ticketId}/participants/${userId}`);
        if (res?.status === 200) {
          showCrmWorkflowToast(toast, t, 'ServiceTaskDetail.Toast.InvolvementRemoved');
          return res;
        } else {
          throw new Error(JSON.stringify(res?.message || res));
        }
      });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
    } finally {
      setBusy(false);
    }
  };

  const involvedUserRow = useMemo(
    () => p.find((row) => Boolean(row?.involved) || String(row?.role || '').toLowerCase() === 'involved') || null,
    [p],
  );

  const currentInvolved = useMemo(() => {
    if (involvedUserRow) {
      return {
        type: 'user',
        id: Number(involvedUserRow?.user_id),
        name: participantName(involvedUserRow),
      };
    }
    return null;
  }, [involvedUserRow, participantName]);

  const hasCurrentInvolved = currentInvolved != null;
  const showInvolveBody = involveTicket || hasCurrentInvolved;

  useEffect(() => {
    const hasInvolvedUser = involvedUserRow != null;
    setInvolveTicket(hasInvolvedUser);

    if (hasInvolvedUser) {
      setInvolveUser({ id: Number(involvedUserRow?.user_id), name: participantName(involvedUserRow) });
    } else {
      setInvolveUser(null);
    }
  }, [involvedUserRow, participantName]);

  const handleInvolveButtonClick = async () => {
    if (hasCurrentInvolved) {
      await removeUser(currentInvolved.id);
      return;
    }
    if (involveUser) {
      await addUser(involveUser?.id || involveUser || null);
      return;
    }
    toast?.current?.show({ severity: 'warn', summary: t('Common.SelectOption'), life: 2000 });
  };

  return (
    <CrmPanelShell
      embedded={embedded}
      title={embedded ? (t(`CrmCommunication.Participants.PanelTitle.${ticketType}`) || t('CrmCommunication.Detail.Participants')) : t('CrmCommunication.Detail.Participants')}
      help={embedded ? (t(`CrmCommunication.Participants.Help.${ticketType}`) || undefined) : (t(`CrmCommunication.Participants.Help.${ticketType}`) || t('CrmCommunication.Participants.Help'))}
      t={t}
    >
      <div className={`${embedded ? '' : 'mt-3'} flex flex-col`}>
        <div className="flex items-center mb-2">
          <Checkbox
            inputId="crmInvolveTicket"
            checked={involveTicket}
            onChange={(e) => setInvolveTicket(Boolean(e.checked))}
            disabled={readOnly || busy}
            className="mr-2"
          />
          <label htmlFor="crmInvolveTicket" className="font-normal text-md">
            {t(`CrmCommunication.Participants.InvolveAdditional.${ticketType}`) || t('CrmCommunication.Participants.InvolveAdditional')}
          </label>
        </div>

        {showInvolveBody ? (
          <>
            <label className="font-normal text-md mb-1">{t('ServiceTaskDetail.SelectUser')}</label>
            <Dropdown
              value={involveUser}
              onChange={(e) => setInvolveUser(e.value)}
              options={userOptions}
              optionLabel="name"
              optionValue="id"
              filter
              placeholder={t('ServiceTaskDetail.SelectUser')}
              disabled={readOnly || busy || hasCurrentInvolved}
              className="w-60 px-4 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
            />

            {hasCurrentInvolved ? (
              <div className="mt-2 text-sm text-emerald-700">
                {t('CrmCommunication.Participants.CurrentlyInvolved')}: <span className="font-semibold">{currentInvolved.name}</span>
              </div>
            ) : null}

            <button
              type="button"
              onClick={() => void handleInvolveButtonClick()}
              disabled={
                readOnly || busy || (
                  !hasCurrentInvolved && !involveUser
                )
              }
              className="flex justify-center items-center mt-3 w-60 h-9 font-medium px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600 disabled:opacity-25"
            >
              {hasCurrentInvolved ? t('CrmCommunication.Participants.Disconnect') : (t(`CrmCommunication.Participants.Involve.${ticketType}`) || t('CrmCommunication.Participants.Involve'))}
            </button>
          </>
        ) : null}
      </div>
    </CrmPanelShell>
  );
}
